import "./assets/app.css";
import "@persolog/theme/fonts";

import { createApp, ref, Ref } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import router from "./router/router.ts";
import type { KeycloakConfig, KeycloakInitOptions } from "keycloak-js";
import Keycloak from "keycloak-js";
import { createI18n } from "vue-i18n";
import { sharedMessages } from "@persolog/i18n";

// primevue components
import PrimeVue from "primevue/config";
import { persologDefault } from "@persolog/theme";
import { useTheme } from "./composables/useTheme";
import Aura from "@primevue/themes/aura";
import Menubar from "primevue/menubar";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Menu from "primevue/menu";
import DataView from "primevue/dataview";
import DataTable from "primevue/datatable";
import Badge from "primevue/badge";
import OverlayBadge from "primevue/overlaybadge";
import Card from "primevue/card";
import InputChips from "primevue/inputchips";
import Chip from "primevue/chip";
import Checkbox from "primevue/checkbox";
import Tag from "primevue/tag";
import Message from "primevue/message";
import Divider from "primevue/divider";
import Panel from "primevue/panel";
import Dialog from "primevue/dialog";
import ToolTip from "primevue/tooltip";
import Skeleton from "primevue/skeleton";
import Toolbar from "primevue/toolbar";
import ToggleButton from "primevue/togglebutton";
import SelectButton from "primevue/selectbutton";
import TreeSelect from "primevue/treeselect";
import MultiSelect from "primevue/multiselect";
import InputNumber from "primevue/inputnumber";
import ProgressSpinner from "primevue/progressspinner";
import Tabs from "primevue/tabs";
import TabPanel from "primevue/tabpanel";
import TabPanels from "primevue/tabpanels";
import TabList from "primevue/tablist";
import Tab from "primevue/tab";
import Textarea from "primevue/textarea";
import Select from "primevue/select";
import Stepper from "primevue/stepper";
import StepList from "primevue/steplist";
import StepPanels from "primevue/steppanels";
import StepItem from "primevue/stepitem";
import Step from "primevue/step";
import StepPanel from "primevue/steppanel";
import Galleria from "primevue/galleria";
import Column from "primevue/column";
import ConfirmDialog from "primevue/confirmdialog";
import ConfirmPopup from "primevue/confirmpopup";
import SplitButton from "primevue/splitbutton";
import Toast from "primevue/toast";
import SpeedDial from "primevue/speeddial";
import Avatar from "primevue/avatar";
import FileUpload from "primevue/fileupload";
import ToggleSwitch from "primevue/toggleswitch";
import Timeline from "primevue/timeline";
import Accordion from "primevue/accordion";
import AccordionHeader from "primevue/accordionheader";
import AccordionContent from "primevue/accordioncontent";
import AccordionPanel from "primevue/accordionpanel";
import InputGroup from "primevue/inputgroup";
import TieredMenu from "primevue/tieredmenu";
import FloatLabel from "primevue/floatlabel";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import ScrollTop from "primevue/scrolltop";
import Fieldset from "primevue/fieldset";
import Slider from "primevue/slider";
import Carousel from "primevue/carousel";
import Image from "primevue/image";
import Inplace from "primevue/inplace";
import Popover from "primevue/popover";
import IftaLabel from "primevue/iftalabel";
import RadioButton from "primevue/radiobutton";
import DatePicker from "primevue/datepicker";
import InputMask from "primevue/inputmask";

import BadgeDirective from "primevue/badgedirective";
import ConfirmationService from "primevue/confirmationservice";
import ToastService from "primevue/toastservice";

export function refArray<T>(): Ref<T[]> {
    return ref([]) as Ref<T[]>;
}

export const i18n = createI18n({
    legacy: false,
    locale: "de",
    fallbackLocale: "en",
    globalInjection: true,
    inheriteLocale: true,
    useScope: "global",
    messages: {
        de: sharedMessages.de,
        en: sharedMessages.en,
    },
});

const storedLang = localStorage.getItem("selectedLanguage");
if (storedLang && i18n.global.availableLocales.includes(storedLang as any)) {
    i18n.global.locale.value = storedLang as any;
}

const config: KeycloakConfig = {
    url: import.meta.env.VITE_APP_KEYCLOAK_URL,
    realm: import.meta.env.VITE_APP_KEYCLOAK_REALM,
    clientId: "administration-frontend",
};

const initOptions: KeycloakInitOptions = {
    onLoad: "check-sso",
    checkLoginIframe: false,
    scope: "email openid roles",
};

export const getDisplayName = (lang: string) => {
    const baseLang = lang.split("_")[0]; // Extract base language, e.g., "zh" from "zh-CN"
    const region = lang.split("_")[1]; // Extract region, e.g., "CN" from "zh-CN"

    const displayName = new Intl.DisplayNames([i18n.global.locale.value], { type: "language" }).of(baseLang);

    if (region) {
        let regionName = new Intl.DisplayNames([i18n.global.locale.value], { type: "region" }).of(region);
        if (region === "CN") {
            regionName = "Simplified";
        }
        if (region === "TW") {
            regionName = "Traditional";
        }
        return `${displayName} (${regionName})`;
    }

    return displayName;
};

export const keycloak = new Keycloak(config);

try {
    const auth = await keycloak.init(initOptions);
    console.log("Authentication status:", auth);

    // Proactively refresh the access token (5 min lifespan) so it stays valid during
    // long editing sessions, e.g. in text maintenance. On failure we only warn instead
    // of redirecting to login: a redirect would destroy the user's unsaved changes.
    setInterval(async () => {
        if (keycloak.authenticated) {
            try {
                const refreshed = await keycloak.updateToken(30);
                if (refreshed) {
                    console.log("Token successfully refreshed");
                }
            } catch (err) {
                console.warn("Token refresh failed", err);
            }
        }
    }, 60_000);

    /** Verifies authentication and fetch trainer details */
    if (auth) {
        console.log("Authenticated");
    }

    const app = createApp(App);

    // Make the keycloak instance available throughout your Vue components
    app.config.globalProperties.$keycloak = keycloak;


    app.use(createPinia())
        .use(i18n)
        .use(router)
        .use(PrimeVue, {
            theme: {
                base: Aura,
                preset: persologDefault,
                options: {
                    prefix: "p",
                    darkModeSelector: "light",
                    // PrimeVue must sit in a layer BELOW Tailwind's utilities: unlayered CSS
                    // always beats layered CSS regardless of specificity, so with cssLayer
                    // disabled every Tailwind utility silently lost against a component style.
                    cssLayer: {
                        name: "primevue",
                        order: "theme, base, asciidoc, primevue, components, utilities",
                    },
                },
            },
            ripple: true,
        });

    // add components
    app.component("Menubar", Menubar);
    app.component("InputText", InputText);
    app.component("Button", Button);
    app.component("Menu", Menu);
    app.component("DataView", DataView);
    app.component("Card", Card);
    app.component("InputChips", InputChips);
    app.component("Checkbox", Checkbox);
    app.component("Chip", Chip);
    app.component("Badge", Badge);
    app.component("OverlayBadge", OverlayBadge);
    app.component("Tag", Tag);
    app.component("Message", Message);
    app.component("Divider", Divider);
    app.component("Panel", Panel);
    app.component("Dialog", Dialog);
    app.component("ScrollTop", ScrollTop);
    app.component("Skeleton", Skeleton);
    app.component("Toolbar", Toolbar);
    app.component("ToggleButton", ToggleButton);
    app.component("SelectButton", SelectButton);
    app.component("TreeSelect", TreeSelect);
    app.component("MultiSelect", MultiSelect);
    app.component("InputNumber", InputNumber);
    app.component("ProgressSpinner", ProgressSpinner);
    app.component("Tabs", Tabs);
    app.component("TabPanel", TabPanel);
    app.component("TabPanels", TabPanels);
    app.component("TabList", TabList);
    app.component("Tab", Tab);
    app.component("ToggleSwitch", ToggleSwitch);
    app.component("Textarea", Textarea);
    app.component("DataTable", DataTable);
    app.component("Column", Column);
    app.component("Galleria", Galleria);
    app.component("ConfirmDialog", ConfirmDialog);
    app.component("ConfirmPopup", ConfirmPopup);
    app.component("SplitButton", SplitButton);
    app.component("Toast", Toast);
    app.component("SpeedDial", SpeedDial);
    app.component("Avatar", Avatar);
    app.component("FileUpload", FileUpload);
    app.component("Timeline", Timeline);
    app.component("Accordion", Accordion);
    app.component("AccordionHeader", AccordionHeader);
    app.component("AccordionContent", AccordionContent);
    app.component("AccordionPanel", AccordionPanel);
    app.component("InputGroup", InputGroup);
    app.component("TieredMenu", TieredMenu);
    app.component("FloatLabel", FloatLabel);
    app.component("IconField", IconField);
    app.component("InputIcon", InputIcon);
    app.component("ScrollTop", ScrollTop);
    app.component("Fieldset", Fieldset);
    app.component("Select", Select);
    app.component("Slider", Slider);
    app.component("Stepper", Stepper);
    app.component("StepList", StepList);
    app.component("StepPanels", StepPanels);
    app.component("StepItem", StepItem);
    app.component("Step", Step);
    app.component("StepPanel", StepPanel);
    app.component("Carousel", Carousel);
    app.component("Image", Image);
    app.component("Inplace", Inplace);
    app.component("Popover", Popover);
    app.component("IftaLabel", IftaLabel);
    app.component("RadioButton", RadioButton);
    app.component("DatePicker", DatePicker);
    app.component("InputMask", InputMask);

    app.directive("badge", BadgeDirective);
    app.directive("tooltip", ToolTip);

    app.use(ConfirmationService);
    app.use(ToastService);

    // Apply the persisted theme (default / high-contrast) before mounting
    useTheme();

    app.mount("#app");
} catch (error: any) {
    if (error?.error === "login_required") {
        globalThis.location.reload();
    } else {
        console.error("Authenticated Failed");
        console.error(error);
    }
}
